import React from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { gql } from '@apollo/client';
import Breadcrumb from '../components/Breadcrumb';
import M3Card from '../components/M3Card';
import M3LinearProgress from '../components/M3Progress';
import {
  DELETE_TEAM,
  ADD_TEAM_MEMBER,
  REMOVE_TEAM_MEMBER,
  ADD_TEAM_PUBLICATION,
  REMOVE_TEAM_PUBLICATION
} from '../graphql/mutations';
import {
  TEAMS_QUERY,
  AVAILABLE_TEAM_MEMBERS_QUERY,
  AVAILABLE_TEAM_PUBLICATIONS_QUERY
} from '../graphql/queries';

const TEAM_QUERY = gql`
  query Team($id: ID!) {
    team(id: $id) {
      id
      name
      description
      users {
        id
        name
        email
      }
      publications {
        id
        title
        author
      }
      userTeams {
        id
        userId
      }
      publicationTeams {
        id
        publicationId
      }
      createdAt
    }
  }
`;

export default function TeamShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(TEAM_QUERY, { variables: { id } });

  const [deleteTeam] = useMutation(DELETE_TEAM, {
    refetchQueries: [{ query: TEAMS_QUERY }, { query: TEAM_QUERY, variables: { id } }],
    async onCompleted() {
      navigate('/teams');
    }
  });

  const [addTeamMember] = useMutation(ADD_TEAM_MEMBER, {
    refetchQueries: [{ query: TEAM_QUERY, variables: { id } }]
  });

  const [removeTeamMember] = useMutation(REMOVE_TEAM_MEMBER, {
    refetchQueries: [{ query: TEAM_QUERY, variables: { id } }]
  });

  const [addTeamPublication] = useMutation(ADD_TEAM_PUBLICATION, {
    refetchQueries: [{ query: TEAM_QUERY, variables: { id } }]
  });

  const [removeTeamPublication] = useMutation(REMOVE_TEAM_PUBLICATION, {
    refetchQueries: [{ query: TEAM_QUERY, variables: { id } }]
  });

  const [newMemberEmail, setNewMemberEmail] = React.useState('');
  const [newPublicationId, setNewPublicationId] = React.useState('');

  const { data: membersData } = useQuery(AVAILABLE_TEAM_MEMBERS_QUERY, {
    variables: { teamId: id },
    skip: !id
  });

  const { data: publicationsData } = useQuery(AVAILABLE_TEAM_PUBLICATIONS_QUERY, {
    variables: { teamId: id },
    skip: !id
  });

  if (error)
    return (
      <div className="page-content">
        <p className="alert alert-danger">Error: {error.message}</p>
      </div>
    );

  if (loading)
    return (
      <div className="page-content">
        <M3LinearProgress indeterminate />
      </div>
    );

  const team = data?.team;
  if (!team) return <div className="page-content">Team not found.</div>;

  return (
    <div className="page-content detail-page">
      <Breadcrumb items={[{ label: 'Teams', link: '/teams' }, { label: team.name }]} />

      <div className="detail-header">
        <h1>{team.name}</h1>
        <div className="detail-actions">
          <Link to={`/teams/${id}/edit`} className="btn btn-secondary">
            <span className="material-symbols-rounded">edit</span>
            Edit
          </Link>
          <button
            className="btn btn-text text-error"
            onClick={() => {
              if (confirm('Delete this team? This cannot be undone.'))
                deleteTeam({ variables: { input: { id } } });
            }}
          >
            <span className="material-symbols-rounded">delete</span>
            Delete
          </button>
        </div>
      </div>

      {team.description && (
        <M3Card variant="outlined">
          <M3Card.Header title="Description" />
          <M3Card.Body>
            <p>{team.description}</p>
          </M3Card.Body>
        </M3Card>
      )}

      <M3Card variant="outlined">
        <M3Card.Header title={`Members (${team.users?.length || 0})`} />
        <M3Card.Body>
          {team.users?.length > 0 && (
            <table className="viz-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {team.users.map((user) => {
                  const membership = team.userTeams?.find((ut) => ut.userId === user.id);
                  return (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        {membership && (
                          <button
                            className="btn btn-sm btn-text text-error"
                            onClick={() => {
                              if (confirm(`Remove ${user.name} from this team?`)) {
                                removeTeamMember({
                                  variables: { teamId: id, membershipId: membership.id }
                                });
                              }
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="d-flex gap-2 align-items-end mt-3">
            <div className="flex-grow-1">
              <label className="form-label">Add Member by Email</label>
              <input
                type="email"
                className="form-control"
                placeholder="Enter user email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
              />
            </div>
            <button
              className="btn btn-success"
              onClick={async () => {
                if (!newMemberEmail) return;
                const users = membersData?.availableTeamMembers || [];
                const user = users.find((u) => u.email === newMemberEmail);
                if (!user) {
                  alert('User not found or already a member.');
                  return;
                }
                await addTeamMember({ variables: { teamId: id, userId: user.id } });
                setNewMemberEmail('');
              }}
            >
              Add User
            </button>
          </div>
        </M3Card.Body>
      </M3Card>

      <M3Card variant="outlined">
        <M3Card.Header title={`Publications (${team.publications?.length || 0})`} />
        <M3Card.Body>
          {team.publications?.length > 0 && (
            <table className="viz-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Year</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {team.publications.map((pub) => {
                  const assignment = team.publicationTeams?.find(
                    (pt) => pt.publicationId === pub.id
                  );
                  return (
                    <tr key={pub.id}>
                      <td>
                        <Link to={`/publications/${pub.id}`}>{pub.title}</Link>
                      </td>
                      <td>{pub.author}</td>
                      <td>{pub.year}</td>
                      <td>
                        {assignment && (
                          <button
                            className="btn btn-sm btn-text text-error"
                            onClick={() => {
                              if (confirm(`Remove "${pub.title}" from this team?`)) {
                                removeTeamPublication({
                                  variables: { teamId: id, assignmentId: assignment.id }
                                });
                              }
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="d-flex gap-2 align-items-end mt-3">
            <div className="flex-grow-1">
              <label className="form-label">Add Publication</label>
              <select
                className="form-select"
                value={newPublicationId}
                onChange={(e) => setNewPublicationId(e.target.value)}
              >
                <option value="">Select a publication...</option>
                {(publicationsData?.availableTeamPublications || []).map((pub) => (
                  <option key={pub.id} value={pub.id}>
                    {pub.title} ({pub.author}, {pub.year})
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-success"
              disabled={!newPublicationId}
              onClick={async () => {
                if (!newPublicationId) return;
                await addTeamPublication({
                  variables: { teamId: id, publicationId: newPublicationId }
                });
                setNewPublicationId('');
              }}
            >
              Add Publication
            </button>
          </div>
        </M3Card.Body>
      </M3Card>
    </div>
  );
}
